import React, { useState, useEffect } from "react";
import {
  AppBar,
  Box,
  Toolbar,
  Typography,
  Button,
  Container,
  IconButton,
  Drawer,
  List,
  ListItem,
  ListItemText,
  useScrollTrigger,
  Slide,
  useMediaQuery,
  useTheme,
  Divider,
  Stack,
  ListItemIcon,
  Tooltip,
  Paper,
  Avatar,
  Collapse,
  SwipeableDrawer,
} from "@mui/material";
import { alpha } from "@mui/material/styles";
import MenuIcon from "@mui/icons-material/Menu";
import HomeIcon from "@mui/icons-material/Home";
import InfoIcon from "@mui/icons-material/Info";
import CategoryIcon from "@mui/icons-material/Category";
import ContactMailIcon from "@mui/icons-material/ContactMail";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import CloseIcon from "@mui/icons-material/Close";
import PhoneIcon from "@mui/icons-material/Phone";
import EmailIcon from "@mui/icons-material/Email";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import { useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import LanguageSwitcher from "./LanguageSwitcher";
import { motion, AnimatePresence } from "framer-motion";

// Hide AppBar on scroll
function HideOnScroll(props) {
  const { children, window } = props;
  const trigger = useScrollTrigger({
    target: window ? window() : undefined,
  });

  return (
    <Slide appear={false} direction="down" in={!trigger}>
      {children}
    </Slide>
  );
}

const Header = (props) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const isMedium = useMediaQuery(theme.breakpoints.down("lg"));
  const isLarge = useMediaQuery(theme.breakpoints.up("xl"));

  const [drawerOpen, setDrawerOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [productsMenuOpen, setProductsMenuOpen] = useState(false);
  const [activeSubMenu, setActiveSubMenu] = useState(null);

  // Check if we're on About or Contact page for fixed style
  const isAboutPage = location.pathname.includes("/about");
  const isContactPage = location.pathname.includes("/contact");
  const isQuotePage = location.pathname.includes("/quote");

  const isFixedStylePage =
    isAboutPage ||
    isContactPage ||
    isQuotePage ||
    location.pathname.startsWith("/products/");

  // Custom gray color scheme
  const grayPalette = {
    light: "#a0a0a0",
    main: "#2c3e50",
    dark: "#494949",
    contrastText: "#ffffff",
  };

  // Product items
  const productItems = [
    {
      id: "barley",
      name: { tr: "Arpa", en: "Barley" },
      image: "/images/barley.jpeg",
      path: "/products/barley",
    },
    {
      id: "wheat",
      name: { tr: "Buğday", en: "Wheat" },
      image: "/images/wheat.jpeg",
      path: "/products/wheat",
    },
    {
      id: "corn",
      name: { tr: "Mısır", en: "Corn" },
      image: "/images/corn.jpeg",
      path: "/products/corn",
    },
    {
      id: "corn-oil",
      name: { tr: "Mısır Yağı", en: "Corn Oil" },
      image: "/images/corn-oil.jpeg",
      path: "/products/corn-oil",
    },
    {
      id: "sunflower-oil",
      name: { tr: "Ayçiçek Yağı", en: "Sunflower Oil" },
      image: "/images/sunflower-oil.jpeg",
      path: "/products/sunflower-oil",
    },
    {
      id: "chicken",
      name: { tr: "Tavuk", en: "Chicken" },
      image: "/images/chicken.jpeg",
      path: "/products/chicken",
    },
    {
      id: "fertilizer",
      name: { tr: "Gübre", en: "Fertilizer" },
      image: "/images/fertilizer.jpeg",
      path: "/products/fertilizer",
    },
  ];

  // Navigation items with icons
  const navItems = [
    { name: t("nav.home"), path: "/", icon: <HomeIcon /> },
    { name: t("nav.about"), path: "/about", icon: <InfoIcon /> },
    {
      name: t("nav.products")
        .replace("Ürünlerimiz", "ÜRÜNLERİMİZ")
        .replace("Our Products", "OUR PRODUCTS")
        .replace("I", "İ"),
      path: "/products",
      icon: <CategoryIcon />,
      hasSubmenu: true,
      submenu: productItems.map((item) => ({
        name: item.name[localStorage.getItem("i18nextLng") || "en"],
        path: item.path,
        image: item.image,
      })),
    },
    {
      name: t("nav.contact")
        .replace("ILETISIM", "İLETİŞİM")
        .replace("İletişim", "İLETİŞİM"),
      path: "/contact",
      icon: <ContactMailIcon />,
    },
  ];

  // Check if scrolled to add box shadow and background
  useEffect(() => {
    // For About and Contact pages, immediately set scrolled to true
    if (isFixedStylePage) {
      setScrolled(true);
      return;
    }

    // For other pages, use normal scroll behavior
    const handleScroll = () => {
      if (window.scrollY > 100) {
        setScrolled(true);
      } else {
        setScrolled(false);
      }
    };

    window.addEventListener("scroll", handleScroll);
    // Initial check
    handleScroll();

    return () => window.removeEventListener("scroll", handleScroll);
  }, [isFixedStylePage]);

  // Function to go to homepage top
  const goToHomeTop = () => {
    navigate("/");
    // Use timeout to ensure navigation completes first
    setTimeout(() => {
      window.scrollTo({
        top: 0,
        behavior: "auto", // Use "auto" for immediate scrolling
      });
    }, 10);
  };

  const handleNavigate = (path) => {
    // For homepage, ensure we scroll to top
    if (path === "/") {
      goToHomeTop();
    } else {
      navigate(path);
    }
    setDrawerOpen(false);
    setProductsMenuOpen(false);
    setActiveSubMenu(null);
  };

  const toggleDrawer = (open) => (event) => {
    if (
      event &&
      event.type === "keydown" &&
      (event.key === "Tab" || event.key === "Shift")
    ) {
      return;
    }

    // Eğer zaten açıksa ve tekrar hamburger butonuna tıklanırsa kapat
    if (drawerOpen && open) {
      setDrawerOpen(false);
      return;
    }

    setDrawerOpen(open);
    if (!open) {
      // Reset submenu state when closing drawer
      setProductsMenuOpen(false);
      setActiveSubMenu(null);
    }
  };

  // Is the current path active?
  const isActive = (path) => {
    if (path === "/") {
      return location.pathname === path;
    }
    return location.pathname.startsWith(path);
  };

  const handleProductsMenuToggle = () => {
    setProductsMenuOpen(!productsMenuOpen);
  };

  const handleSubMenuClick = (index) => {
    setActiveSubMenu(activeSubMenu === index ? null : index);
  };

  // Animation variants for mobile menu items
  const mobileMenuVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: (i) => ({
      opacity: 1,
      y: 0,
      transition: {
        delay: i * 0.05,
        duration: 0.3,
      },
    }),
    exit: {
      opacity: 0,
      y: -20,
      transition: {
        duration: 0.2,
      },
    },
  };

  // Get contact info JSX
  const getContactInfo = (direction = "row", showLabels = false) => (
    <Box
      sx={{
        display: "flex",
        flexDirection: direction,
        alignItems: direction === "row" ? "center" : "flex-start",
        gap: 2,
      }}
    >
      <Tooltip title={showLabels ? "" : t("nav.phone")}>
        <Typography
          variant="body2"
          component="a"
          href="tel:+90 533 399 2188"
          sx={{
            color: "#fff",
            textDecoration: "none",
            fontSize: "0.85rem",
            display: "flex",
            alignItems: "center",
            transition: "all 0.3s ease",
            "&:hover": {
              color: theme.palette.secondary.light,
            },
          }}
        >
          <PhoneIcon sx={{ fontSize: 18, mr: 0.5 }} />
          {showLabels && <span>{t("nav.phone")}: </span>}
          +90 533 399 2188
        </Typography>
      </Tooltip>

      <Tooltip title={showLabels ? "" : t("nav.email")}>
        <Typography
          variant="body2"
          component="a"
          href="mailto:info@sgttr.com"
          sx={{
            color: "#fff",
            textDecoration: "none",
            fontSize: "0.85rem",
            display: "flex",
            alignItems: "center",
            transition: "all 0.3s ease",
            "&:hover": {
              color: theme.palette.secondary.light,
            },
          }}
        >
          <EmailIcon sx={{ fontSize: 18, mr: 0.5 }} />
          {showLabels && <span>{t("nav.email")}: </span>}
          info@sgttr.com
        </Typography>
      </Tooltip>

      {!isMobile && (
        <Tooltip title={showLabels ? "" : t("nav.address")}>
          <Typography
            variant="body2"
            sx={{
              color: "#fff",
              fontSize: "0.85rem",
              display: "flex",
              alignItems: "center",
            }}
          >
            <LocationOnIcon sx={{ fontSize: 18, mr: 0.5 }} />
            {showLabels && <span>{t("nav.address")}: </span>}
            İNKILAP MH. SEYİT ONBAŞI CD. NO: 7/A ÜMRANİYE / İSTANBUL
          </Typography>
        </Tooltip>
      )}
    </Box>
  );

  // Create mobile drawer content - Modernleştirilmiş çekmece içeriği
  const drawerContent = (
    <Box sx={{ width: "280px", height: "100%" }}>
      {/* Header - Çarpı ikonu kaldırıldı */}
      <Box
        sx={{
          py: 2.2,
          px: 2,
          display: "flex",
          justifyContent: "flex-start",
          alignItems: "center",
          borderBottom: `1px solid ${alpha("#fff", 0.1)}`,
          background: `linear-gradient(135deg, ${alpha(
            theme.palette.secondary.dark,
            0.5
          )}, ${alpha(grayPalette.main, 0.9)})`,
          boxShadow: "0 2px 8px rgba(0,0,0,0.2)",
          borderTopLeftRadius: "12px", // Üst köşeyi yuvarlatma
        }}
      >
        <Box
          component="img"
          src="/images/sgt_logo.png"
          alt="SGT Logo"
          sx={{
            height: 36, // Biraz küçülttük
            mr: 1.5,
            filter: "drop-shadow(0px 2px 3px rgba(0,0,0,0.4))",
          }}
        />
        <Box>
          <Typography
            variant="h6"
            sx={{
              color: "#fff",
              fontWeight: 700,
              letterSpacing: 0.5,
              fontSize: "0.95rem", // Biraz küçülttük
              textShadow: "0px 1px 2px rgba(0,0,0,0.4)",
            }}
          >
            SAFE GLOBAL TRADE
          </Typography>
          <Typography
            variant="caption"
            sx={{
              color: "#fff",
              opacity: 0.85,
              display: "block",
              fontSize: "0.65rem",
              fontWeight: 400,
              letterSpacing: 0.3,
            }}
          >
            International Trade & Supply Chain
          </Typography>
        </Box>
      </Box>

      {/* Menu Items - Geliştirilmiş stil */}
      <Box sx={{ overflow: "auto", height: "calc(100% - 80px)" }}>
        <List sx={{ pt: 1.5, pb: 0 }}>
          {navItems.map((item, index) => (
            <React.Fragment key={item.path}>
              <motion.div
                initial={{ x: -20, opacity: 0 }}
                animate={{ x: 0, opacity: 1 }}
                transition={{ delay: index * 0.1, duration: 0.3 }}
              >
                <ListItem
                  onClick={() => {
                    if (item.hasSubmenu) {
                      handleSubMenuClick(index);
                    } else {
                      handleNavigate(item.path);
                    }
                  }}
                  selected={isActive(item.path)}
                  tabIndex={0}
                  sx={{
                    py: 1.7,
                    borderRadius: 1,
                    mx: 1,
                    mb: 0.5,
                    color: "#fff",
                    position: "relative",
                    borderLeft: isActive(item.path)
                      ? `3px solid ${theme.palette.secondary.main}`
                      : "3px solid transparent",
                    pl: 2,
                    backgroundColor: isActive(item.path)
                      ? alpha("#fff", 0.08)
                      : "transparent",
                    "&:hover": {
                      backgroundColor: alpha("#fff", 0.05),
                      transform: "translateX(5px)",
                      transition: "transform 0.2s ease",
                    },
                    transition: "all 0.25s ease",
                    cursor: "pointer",
                  }}
                >
                  <ListItemIcon
                    sx={{
                      color: isActive(item.path)
                        ? theme.palette.secondary.main
                        : "#fff",
                      minWidth: 36,
                    }}
                  >
                    {item.icon}
                  </ListItemIcon>
                  <ListItemText
                    primary={item.name}
                    primaryTypographyProps={{
                      fontWeight: isActive(item.path) ? 600 : 500,
                      fontSize: "0.9rem",
                      fontFamily: "'Montserrat', sans-serif",
                    }}
                  />
                  {item.hasSubmenu && (
                    <KeyboardArrowDownIcon
                      sx={{
                        transform:
                          activeSubMenu === index
                            ? "rotate(180deg)"
                            : "rotate(0)",
                        transition: "transform 0.3s ease",
                        color:
                          activeSubMenu === index
                            ? theme.palette.secondary.main
                            : "inherit",
                        fontSize: "1.2rem",
                      }}
                    />
                  )}
                </ListItem>
              </motion.div>

              {item.hasSubmenu && (
                <Collapse
                  in={activeSubMenu === index}
                  timeout="auto"
                  unmountOnExit
                >
                  <List
                    component="div"
                    disablePadding
                    sx={{
                      backgroundColor: alpha("#000", 0.15),
                      mx: 1,
                      borderRadius: 1,
                      overflow: "hidden",
                      mb: 1.5,
                    }}
                  >
                    {item.submenu.map((subItem, subIndex) => (
                      <motion.div
                        key={subItem.path}
                        initial={{ opacity: 0, y: -10 }}
                        animate={{ opacity: 1, y: 0 }}
                        transition={{ delay: subIndex * 0.05, duration: 0.25 }}
                      >
                        <ListItem
                          onClick={() => handleNavigate(subItem.path)}
                          tabIndex={0}
                          sx={{
                            py: 1.2,
                            pl: 7,
                            pr: 2,
                            color: "#fff",
                            borderBottom:
                              subIndex < item.submenu.length - 1
                                ? `1px solid ${alpha("#fff", 0.05)}`
                                : "none",
                            "&:hover": {
                              backgroundColor: alpha("#000", 0.2),
                              "& .MuiAvatar-root": {
                                transform: "scale(1.1)",
                                transition: "transform 0.2s ease",
                              },
                            },
                            cursor: "pointer",
                          }}
                        >
                          <Avatar
                            src={subItem.image}
                            variant="rounded"
                            sx={{
                              width: 24,
                              height: 24,
                              mr: 1.5,
                              transition: "transform 0.2s ease",
                            }}
                          />
                          <ListItemText
                            primary={subItem.name}
                            primaryTypographyProps={{
                              fontSize: "0.85rem",
                              fontWeight: 500,
                            }}
                          />
                        </ListItem>
                      </motion.div>
                    ))}
                  </List>
                </Collapse>
              )}
            </React.Fragment>
          ))}
        </List>
        <Divider sx={{ borderColor: alpha("#fff", 0.1), my: 1.5 }} />
        {/* Quote Button - Geliştirilmiş stil */}
        <Box sx={{ px: 2, pt: 1, pb: 2 }}>
          <motion.div
            initial={{ y: 20, opacity: 0 }}
            animate={{ y: 0, opacity: 1 }}
            transition={{ delay: 0.3, duration: 0.4 }}
          >
            <Button
              variant="contained"
              fullWidth
              startIcon={<RequestQuoteIcon />}
              onClick={() => handleNavigate("/quote")}
              sx={{
                fontWeight: 600,
                textTransform: "none",
                py: 1.2,
                borderRadius: "6px",
                background: `linear-gradient(145deg, ${theme.palette.secondary.main}, ${theme.palette.secondary.dark})`,
                boxShadow: "0 4px 12px rgba(0,0,0,0.2)",
                "&:hover": {
                  background: `linear-gradient(145deg, ${theme.palette.secondary.light}, ${theme.palette.secondary.main})`,
                  transform: "translateY(-2px)",
                  boxShadow: "0 6px 15px rgba(0,0,0,0.25)",
                },
                transition: "all 0.3s ease",
              }}
            >
              {t("nav.getQuote")}
            </Button>
          </motion.div>
        </Box>
        {/* Hamburger menü içeriğinin iletişim bölümü - adres bilgisi kaldırılmış */}
        <Box
          sx={{
            px: 2,
            pb: 3,
            pt: 2,
            mt: 1,
            background: `linear-gradient(to bottom, ${alpha(
              "#000",
              0.2
            )}, ${alpha("#000", 0.1)})`,
          }}
        >
          <Typography
            variant="subtitle2"
            sx={{
              color: "#fff",
              mb: 1.5,
              opacity: 0.9,
              fontWeight: 600,
              fontSize: "0.8rem",
              letterSpacing: 0.5,
              textTransform: "uppercase",
            }}
          >
            {t("nav.contactUs")}
          </Typography>

          <Box sx={{ display: "flex", flexDirection: "column", gap: 1.5 }}>
            <motion.div
              initial={{ x: -10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.4, duration: 0.3 }}
            >
              <Typography
                variant="body2"
                component="a"
                href="tel:+90 533 399 2188"
                sx={{
                  color: "#fff",
                  textDecoration: "none",
                  fontSize: "0.8rem",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    color: theme.palette.secondary.light,
                  },
                  transition: "color 0.2s ease",
                }}
              >
                <PhoneIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    color: theme.palette.secondary.light,
                  }}
                />
                +90 533 399 2188
              </Typography>
            </motion.div>

            <motion.div
              initial={{ x: -10, opacity: 0 }}
              animate={{ x: 0, opacity: 1 }}
              transition={{ delay: 0.5, duration: 0.3 }}
            >
              <Typography
                variant="body2"
                component="a"
                href="mailto:info@sgttr.com"
                sx={{
                  color: "#fff",
                  textDecoration: "none",
                  fontSize: "0.8rem",
                  display: "flex",
                  alignItems: "center",
                  "&:hover": {
                    color: theme.palette.secondary.light,
                  },
                  transition: "color 0.2s ease",
                }}
              >
                <EmailIcon
                  sx={{
                    fontSize: 16,
                    mr: 1,
                    color: theme.palette.secondary.light,
                  }}
                />
                info@sgttr.com
              </Typography>
            </motion.div>

            {/* Adres bilgisi kaldırıldı */}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <HideOnScroll {...props}>
      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          backgroundColor: scrolled
            ? alpha(grayPalette.main, 0.85)
            : "transparent",
          backgroundImage: scrolled
            ? `linear-gradient(180deg, rgba(44,62,80,0.75), rgba(44,62,80,0.85)), url('/images/1920_500wheatfield.jpg')`
            : "none",
          backgroundSize: "cover",
          backgroundPosition: "center",
          backgroundRepeat: "no-repeat",
          backdropFilter: scrolled ? "blur(10px)" : "none",
          transition: "all 0.4s ease",
          boxShadow: scrolled ? `0 4px 20px rgba(0,0,0,0.15)` : "none",
          zIndex: 1300,
        }}
      >
        {/* Contact Info Top Bar (only visible on large screens and when scrolled) */}
        {isLarge && scrolled && (
          <Box
            sx={{
              backgroundColor: alpha("#000", 0.15),
              py: 0.5,
            }}
          >
            <Container maxWidth="xl">
              <Box
                sx={{
                  display: "flex",
                  justifyContent: "space-between",
                  alignItems: "center",
                }}
              >
                {getContactInfo()}
                
                <Box sx={{ display: "flex", alignItems: "center", gap: 2 }}>
                  <LanguageSwitcher compact />
                  <Button
                    variant="outlined"
                    size="small"
                    startIcon={<RequestQuoteIcon />}
                    onClick={() => handleNavigate("/quote")}
                    sx={{
                      color: "#fff",
                      borderColor: "#fff",
                      textTransform: "none",
                      fontSize: "0.75rem",
                      "&:hover": {
                        borderColor: "#fff",
                        backgroundColor: alpha("#fff", 0.1),
                      },
                    }}
                  >
                    {t("nav.getQuote")}
                  </Button>
                </Box>
              </Box>
            </Container>
          </Box>
        )}

        {/* Main Navigation Bar */}
        <Container maxWidth="xl">
          <Toolbar
            disableGutters
            sx={{
              minHeight: { xs: 65, sm: 70, md: 80 },
              py: { xs: 1, md: 0 },
              justifyContent: "space-between",
            }}
          >
            {/* Logo Section */}
            <motion.div
              initial={{ opacity: 0, x: -20 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.5 }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  cursor: "pointer",
                  mr: { xs: 0, md: 4 },
                }}
                onClick={goToHomeTop}
              >
                {/* Logo and Name Side by Side */}
                <Box sx={{ display: "flex", alignItems: "center" }}>
                  {/* Logo Image */}
                  <Box
                    component="img"
                    src="/images/sgt_logo.png"
                    alt="SGT Logo"
                    sx={{
                      height: { xs: 42, sm: 45, md: 50 },
                      mr: 2,
                      transition: "all 0.3s ease",
                      filter: "drop-shadow(0px 2px 4px rgba(0,0,0,0.5))",
                    }}
                  />

                  {/* Company Name */}
                  <Box
                    sx={{
                      display: "flex",
                      flexDirection: "column",
                    }}
                  >
                    <Typography
                      variant="h6"
                      component="div"
                      sx={{
                        fontWeight: 800,
                        color: "#fff",
                        letterSpacing: 0.5,
                        fontSize: { xs: "1.1rem", sm: "1.3rem", md: "1.5rem" },
                        lineHeight: 1.2,
                        textShadow: "0px 2px 4px rgba(0,0,0,0.5)",
                      }}
                    >
                      SAFE GLOBAL TRADE
                    </Typography>
                    <Typography
                      variant="subtitle2"
                      component="div"
                      sx={{
                        fontWeight: 400,
                        color: "#f5f5f5",
                        opacity: 0.8,
                        letterSpacing: 1,
                        fontSize: { xs: "0.6rem", sm: "0.7rem" },
                        textShadow: "0px 1px 2px rgba(0,0,0,0.5)",
                      }}
                    >
                      International Trade & Supply Chain
                    </Typography>
                  </Box>
                </Box>
              </Box>
            </motion.div>

            {/* Desktop Navigation */}
            {!isMobile && (
              <Stack
                direction="row"
                spacing={isMedium ? 0.5 : 2}
                alignItems="center"
                sx={{ flexGrow: 1, justifyContent: "center" }}
              >
                {navItems.map((item, index) => (
                  <motion.div
                    key={item.path}
                    initial={{ opacity: 0, y: -20 }}
                    animate={{ opacity: 1, y: 0 }}
                    transition={{ duration: 0.5, delay: index * 0.1 }}
                  >
                    {item.hasSubmenu ? (
                      <Box
                        sx={{
                          position: "relative",
                          "&:hover": {
                            "& .products-menu": {
                              opacity: 1,
                              visibility: "visible",
                              transform: "translateY(0)",
                            },
                          },
                        }}
                      >
                        <Button
                          sx={{
                            color: "#fff",
                            fontWeight: isActive(item.path) ? 600 : 400,
                            fontSize: { md: "0.9rem", lg: "0.95rem" },
                            px: { md: 1, lg: 1.5 },
                            py: 2,
                            position: "relative",
                            textTransform: "none",
                            textShadow: "0px 2px 4px rgba(0,0,0,0.7)",
                            "&::after": {
                              content: '""',
                              position: "absolute",
                              bottom: 8,
                              left: "50%",
                              width: isActive(item.path) ? "30%" : "0%",
                              height: 2,
                              backgroundColor: theme.palette.secondary.main,
                              transition: "all 0.3s ease",
                              transform: "translateX(-50%)",
                              borderRadius: "3px",
                            },
                            "&:hover": {
                              background: "transparent",
                              "&::after": {
                                width: "30%",
                              },
                            },
                          }}
                          endIcon={
                            <KeyboardArrowDownIcon sx={{ fontSize: 18 }} />
                          }
                        >
                          {item.name}
                        </Button>
                        <Paper
                          className="products-menu"
                          role="menu"
                          tabIndex={-1}
                          sx={{
                            position: "absolute",
                            top: "100%",
                            left: 0,
                            right: 0,
                            zIndex: 1,
                            mt: 0.5,
                            width: 300,
                            backgroundColor: alpha("#2c3e50", 0.9),
                            backdropFilter: "blur(10px)",
                            boxShadow: "0 10px 30px rgba(0,0,0,0.25)",
                            borderRadius: "16px",
                            overflow: "hidden",
                            visibility: "hidden",
                            opacity: 0,
                            transform: "translateY(10px)",
                            transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
                            padding: "8px",
                          }}
                        >
                          <List>
                            {item.submenu.map((subItem, i) => (
                              <ListItem
                                key={subItem.path}
                                onClick={() => handleNavigate(subItem.path)}
                                tabIndex={0}
                                sx={{
                                  py: 1,
                                  borderRadius: "10px",
                                  mb: 0.5,
                                  cursor: "pointer",
                                  transition: "all 0.2s ease",
                                  "&:hover": {
                                    backgroundColor: alpha("#fff", 0.1),
                                    transform: "translateX(5px)",
                                    "& .MuiAvatar-root": {
                                      transform: "scale(1.1)",
                                    },
                                  },
                                }}
                              >
                                <Avatar
                                  src={subItem.image}
                                  variant="rounded"
                                  sx={{
                                    width: 32,
                                    height: 32,
                                    mr: 2,
                                    boxShadow: "0 2px 4px rgba(0,0,0,0.2)",
                                    transition: "transform 0.2s ease",
                                  }}
                                />
                                <ListItemText
                                  primary={subItem.name}
                                  primaryTypographyProps={{
                                    color: "#fff",
                                    fontWeight: 500,
                                    fontSize: "0.9rem",
                                  }}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </Paper>
                      </Box>
                    ) : (
                      <Button
                        onClick={() => handleNavigate(item.path)}
                        sx={{
                          color: "#fff",
                          fontWeight: isActive(item.path) ? 600 : 400,
                          fontSize: { md: "0.9rem", lg: "0.95rem" },
                          px: { md: 1, lg: 1.5 },
                          py: 2,
                          position: "relative",
                          textTransform: "uppercase",
                          textShadow: "0px 2px 4px rgba(0,0,0,0.7)",
                          "&::after": {
                            content: '""',
                            position: "absolute",
                            bottom: 8,
                            left: "50%",
                            width: isActive(item.path) ? "30%" : "0%",
                            height: 2,
                            backgroundColor: theme.palette.secondary.main,
                            transition: "all 0.3s ease",
                            transform: "translateX(-50%)",
                            borderRadius: "3px",
                          },
                          "&:hover": {
                            background: "transparent",
                            "&::after": {
                              width: "30%",
                            },
                          },
                        }}
                      >
                        {item.name}
                      </Button>
                    )}
                  </motion.div>
                ))}
              </Stack>
            )}

            {/* Right Actions Section */}
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: { xs: 1, md: 2 },
              }}
            >
              {/* Contact Info (show only on medium screens) */}
              {isMedium && !isMobile && (
                <Box sx={{ display: "flex", gap: 2 }}>
                  <Tooltip title={t("nav.phone")}>
                    <IconButton
                      size="small"
                      href="tel:+90 533 399 2188"
                      sx={{
                        color: "#fff",
                        filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.7))",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          color: theme.palette.secondary.light,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <PhoneIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title={t("nav.email")}>
                    <IconButton
                      size="small"
                      href="mailto:info@sgttr.com"
                      sx={{
                        color: "#fff",
                        filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.7))",
                        transition: "all 0.2s ease",
                        "&:hover": {
                          color: theme.palette.secondary.light,
                          transform: "translateY(-2px)",
                        },
                      }}
                    >
                      <EmailIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </Box>
              )}

              {/* Quote Button (show only on larger screens) */}
              {!isMobile && !isLarge && (
                <motion.div
                  initial={{ opacity: 0, scale: 0.8 }}
                  animate={{ opacity: 1, scale: 1 }}
                  transition={{ duration: 0.5, delay: 0.6 }}
                >
                  <Button
                    variant="contained"
                    color="secondary"
                    startIcon={<RequestQuoteIcon />}
                    onClick={() => handleNavigate("/quote")}
                    sx={{
                      fontWeight: 600,
                      textTransform: "none",
                      borderRadius: "30px",
                      px: 2,
                      py: 0.75,
                      fontSize: "0.85rem",
                      whiteSpace: "nowrap",
                      boxShadow: `0 4px 12px ${alpha("#000", 0.3)}`,
                      transition: "all 0.3s ease",
                      "&:hover": {
                        transform: "translateY(-3px)",
                        boxShadow: `0 8px 16px ${alpha("#000", 0.4)}`,
                      },
                    }}
                  >
                    {t("nav.getQuote")}
                  </Button>
                </motion.div>
              )}

              {/* Language Switcher (all screens) */}
              {!isLarge && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <LanguageSwitcher
                    sx={{
                      filter: "drop-shadow(0px 1px 2px rgba(0,0,0,0.7))",
                      transition: "all 0.2s ease",
                    }}
                  />
                </motion.div>
              )}

              {/* Modernleştirilmiş Mobile Menu Button */}
              {isMobile && (
                <motion.div
                  initial={{ opacity: 0 }}
                  animate={{ opacity: 1 }}
                  transition={{ duration: 0.5, delay: 0.7 }}
                >
                  <IconButton
                    aria-label="menu"
                    onClick={toggleDrawer(!drawerOpen)}
                    sx={{
                      color: "#fff",
                      backgroundColor: drawerOpen
                        ? theme.palette.secondary.main
                        : alpha("#fff", 0.08),
                      "&:hover": {
                        backgroundColor: drawerOpen
                          ? theme.palette.secondary.dark
                          : alpha("#fff", 0.15),
                        transform: drawerOpen ? "rotate(90deg)" : "scale(1.05)",
                      },
                      transition: "all 0.3s ease",
                      padding: "8px",
                      zIndex: 1300, // Drawer'ın zIndex'inden düşük olmalı
                      borderRadius: "8px",
                      boxShadow: drawerOpen
                        ? "0 4px 10px rgba(0,0,0,0.3)"
                        : "none",
                    }}
                  >
                    {drawerOpen ? (
                      <CloseIcon fontSize="small" />
                    ) : (
                      <MenuIcon fontSize="small" />
                    )}
                  </IconButton>
                </motion.div>
              )}
            </Box>

            {/* Mobile Drawer - Modernleştirilmiş SwipeableDrawer */}
            <SwipeableDrawer
              anchor="right"
              open={drawerOpen}
              onClose={toggleDrawer(false)}
              onOpen={toggleDrawer(true)}
              disableBackdropTransition={false}
              disableDiscovery={false}
              ModalProps={{
                keepMounted: true,
              }}
              componentsProps={{
                backdrop: {
                  "aria-hidden": "false",
                },
              }}
              PaperProps={{
                sx: {
                  width: "280px",
                  backgroundColor: alpha(grayPalette.main, 0.98),
                  backdropFilter: "blur(10px)",
                  overflowY: "auto",
                  boxShadow: "-3px 0 15px rgba(0,0,0,0.25)",
                  marginTop: "65px", // Header yüksekliğine göre ayarlandı
                  height: "calc(100% - 65px)", // Yeni margin değerine göre ayarlandı
                  borderTopLeftRadius: "12px", // Yuvarlatılmış köşeler ekledik
                },
              }}
            >
              {drawerContent}
            </SwipeableDrawer>
          </Toolbar>
        </Container>
      </AppBar>
    </HideOnScroll>
  );
};

export default Header;